/**
 * Tipos de error de sincronización.
 *
 * Usados para clasificar errores y determinar cómo manejarlos:
 * - RETRYABLE: Reintentar la operación (network, timeout)
 * - CONFLICT: Descartar cambios locales (already exists, not found)
 * - VALIDATION: Descartar cambios locales (invalid data)
 * - PERMISSION: Propagar error (unauthorized, forbidden)
 * - UNKNOWN: Propagar error (no se pudo clasificar)
 */
export enum SyncErrorType {
  RETRYABLE = "retryable",
  CONFLICT = "conflict",
  VALIDATION = "validation",
  PERMISSION = "permission",
  UNKNOWN = "unknown",
}

/**
 * Verifica si el error es debido a problemas de red (reintentable).
 */
export function isRetryable(message: string): boolean {
  return (
    message.includes("network") ||
    message.includes("timeout") ||
    message.includes("econnrefused") ||
    message.includes("fetch failed")
  );
}

/**
 * Verifica si el error es un conflicto (descartar cambios locales).
 */
export function isConflict(message: string): boolean {
  return (
    message.includes("conflict") ||
    message.includes("already exists") ||
    message.includes("duplicate") ||
    message.includes("not found") ||
    message.includes("404")
  );
}

/**
 * Verifica si el error es de validación (descartar cambios locales).
 */
export function isValidation(message: string): boolean {
  return (
    message.includes("validation") || message.includes("invalid") || message.includes("required")
  );
}

/**
 * Verifica si el error es de permisos (propagar error).
 */
export function isPermission(message: string): boolean {
  return (
    message.includes("unauthorized") ||
    message.includes("forbidden") ||
    message.includes("401") ||
    message.includes("403")
  );
}

/**
 * Clasifica un error según su tipo para determinar cómo manejarlo.
 *
 * @param error - El error a clasificar
 * @returns El tipo de error
 */
export function classifyError(error: unknown): SyncErrorType {
  if (!(error instanceof Error)) {
    return SyncErrorType.UNKNOWN;
  }

  const message = error.message.toLowerCase();

  if (isRetryable(message)) {
    return SyncErrorType.RETRYABLE;
  }

  if (isConflict(message)) {
    return SyncErrorType.CONFLICT;
  }

  if (isValidation(message)) {
    return SyncErrorType.VALIDATION;
  }

  if (isPermission(message)) {
    return SyncErrorType.PERMISSION;
  }

  return SyncErrorType.UNKNOWN;
}
